import React, { createRef, useRef, memo, useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';

import { authSelectors } from 'app-components/flux/auth';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { preferencesV2Selectors } from 'app-components/flux/preferences-v2';

import RendersCount from 'components/RendersCount';
import type { FeaturePage } from 'containers/App/paths';
import { getNavFromKey } from 'containers/App/paths';

import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Slide from '@mui/material/Slide';
import Toolbar from '@mui/material/Toolbar';

import { memorizedRulesSelectors } from 'app-components/flux/memorized-rules';
import { preferencesActions, preferencesSelectors } from 'app-components/flux/preferences';
import { renameRulesSelectors } from 'app-components/flux/rename-rules';

import GuidedTooltip from 'components/GuidedTooltip';

import { makeStyles } from 'tss-react/mui';
import ProfileMenu from './ProfileMenu';

import SimplifiAndQBPLogo from 'components/SimplifiAndQBPLogo';
import { useAppSelector } from 'hooks/redux';
import { useDispatch } from 'react-redux';
import { IntercomNavItem, MainNavItem, NotificationsNavItem, RefreshNavItem, UserGuideNavItem } from './NavItems';
import { NavPopoverV2 } from './Popover';
import { navBarWidth, useNavBarStyle } from './commonNavStyles';

const useStyles = makeStyles()((theme) => ({
  staticNav: {
    zIndex: theme.zIndex.modal,
    backgroundColor: theme.palette.background.paper,
    width: navBarWidth,
    left: 0,
    height: '100%',
  },
  navItemsHolder: {
    display: 'flex',
    flexDirection: 'column-reverse',
    marginTop: 0,
    textAlign: 'left',
    width: '100%',
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 0,
    padding: 0,
    alignItems: 'baseline',
    border: 0,
  },
  rootButton: {
    minWidth: 'unset',
  },
  logoSection: {
    height: theme.defaults?.headers.subHeaderHeightTall,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  navButtonBottom: {
    display: 'flex',
    flexDirection: 'column',
    flexFlow: 'column',
    textAlign: 'center',
    textColor: '#333',
  },
  betaLabel: {
    flex: 'auto',
    fontSize: '18px',
    fontWeight: 200,
    letterSpacing: '.05rem',
    position: 'absolute',
    right: 5,
    top: 10,
  },
  appDiv: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: 52,
    borderRight: `1px solid ${theme.palette.border.light}`,
  },
  appBarContainer: {
    overflowY: 'auto',
    maxWidth: '100%',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.applyOpacityToHex?.(theme.palette.icon.main, 0.5)} ${theme.palette.background.paper}`,
    background: theme.palette.background.paper,
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.applyOpacityToHex?.(theme.palette.icon.main, 0.5),
      borderRadius: 8,
    },
    '&::-webkit-scrollbar': {
      backgroundColor: theme.palette.background.paper,
      width: 5,
    },
  },
  navBottomSection: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: 'auto',
    marginBottom: 32,
    overflow: 'hidden',
    flexShrink: 0,
  },
}));

const settingsNav = getNavFromKey('settings') as FeaturePage;
const docsNav = getNavFromKey('docs') as FeaturePage;

const SettingsPage = React.memo(function SettingsPage({
  closeDrawer,
}: {
  closeDrawer: () => void;
}) {
  const dispatch = useDispatch();
  const settingsPageNavRef = createRef<HTMLDivElement>();
  const dontShowNewRuleTooltip = useAppSelector(
    (state) =>
      preferencesSelectors.getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp'] })
        .dontShowNewRuleTooltip,
  );
  const [showNewRuleTooltip, setShowNewRuleTooltip] = useState(false);
  const renameRulesSize = useAppSelector((state) => renameRulesSelectors.getRulesById(state).size);
  const memorizedRulesSize = useAppSelector((state) => memorizedRulesSelectors.getMemorizedRulesById(state).size);
  const renameRulesSizeRef = useRef(renameRulesSize);
  const memorizedRulesSizeRef = useRef(memorizedRulesSize);

  useEffect(() => {
    if (
      !window.location?.pathname?.includes?.('settings') &&
      !dontShowNewRuleTooltip &&
      (renameRulesSize > renameRulesSizeRef.current || memorizedRulesSize > memorizedRulesSizeRef.current)
    ) {
      // number of rules went up
      setShowNewRuleTooltip(true);
    }
  }, [dontShowNewRuleTooltip, memorizedRulesSize, renameRulesSize]);

  return (
    <>
      <RendersCount id='Header' />
      <MainNavItem
        key='nav-item-settings'
        nav={settingsNav}
        iconRef={settingsPageNavRef}
        displayNode='all'
        closeHeader={closeDrawer}
      />
      <GuidedTooltip
        anchorEl={settingsPageNavRef.current}
        open={showNewRuleTooltip}
        placement='right'
        title='Rule created'
        body='Payee rename and category rules can be edited or deleted in Settings.'
        cancelLabel="don't show again"
        continueLabel='got it'
        onCancel={() => {
          setShowNewRuleTooltip(false);
          dispatch(
            preferencesActions.setPreference(
              {
                section: 'shared',
                group: 'dataset',
                preference: { webApp: { dontShowNewRuleTooltip: true } },
              },
              { context: 'QPreferences:setDatasetPreference' },
            ),
          );
        }}
        onContinue={() => {
          setShowNewRuleTooltip(false);
        }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
        ]}
      />
    </>
  );
});

function HeaderNew() {
  const { classes } = useStyles();
  const { classes: navBarClasses } = useNavBarStyle();
  const [drawerCanOpen, setDrawerCanOpen] = useState(true);
  const closeDrawer = useCallback(() => setDrawerCanOpen(false), []);
  const openDrawer = useCallback(() => setDrawerCanOpen(true), []);
  const authenticated: boolean = useAppSelector((state) => authSelectors.isAuthenticated(state));
  const showIntercom = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'intercom'));
  const hideNavButtons = useAppSelector(
    (state) => preferencesV2Selectors.getPreferenceValue(state, 'gsfScreen', 'completed_gsf') !== 'completed_gsf',
  );
  const kioskmode: boolean = useAppSelector(
    (state) => preferencesSelectors.getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp'] }).kioskmode,
  );

  return (
    <Slide in={authenticated} direction='right'>
      <AppBar
        id='application-header-bar'
        elevation={0}
        position='fixed'
        className={
          hideNavButtons ? classes.staticNav : !drawerCanOpen ? navBarClasses.navbarClosed : navBarClasses.navbar
        }
        onMouseLeave={openDrawer}
      >
        <div className={classes.appDiv}>
          <div className={classes.logoSection} style={{ cursor: hideNavButtons ? 'default' : undefined }}>
            <Button
              onClick={closeDrawer}
              id='logo-nav'
              classes={{
                root: classes.rootButton,
              }}
              component={NavLink}
              to='/'
              disabled={hideNavButtons}
              style={{ cursor: hideNavButtons ? 'default' : undefined }}
            >
              <SimplifiAndQBPLogo showLogo />
            </Button>
          </div>
          <div className={classes.appBarContainer}>
            <Toolbar classes={{ root: classes.toolbar }}>
              <div className={classes.betaLabel}>{`${kioskmode ? '*' : ''}`}</div>

              {!hideNavButtons && (
                <div key='main-nav-items' className={classes.navItemsHolder}>
                  <div>
                    <NavPopoverV2 closeDrawer={closeDrawer} />
                  </div>
                </div>
              )}
            </Toolbar>
          </div>
          <div className={classes.navBottomSection}>
            <div className={classes.navButtonBottom}>
              {
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
                <div style={{ paddingTop: 16, paddingBottom: 16 }}>
                  <MainNavItem key='nav-item-docs' closeHeader={closeDrawer} nav={docsNav} />
                </div>
/////////////////////////
              }

              <div className={navBarClasses.groupDividerStatic} />
              {!hideNavButtons && <RefreshNavItem key='refresh-all-button' closeDrawer={closeDrawer} />}

              {!hideNavButtons && <NotificationsNavItem key='notification-item' closeDrawer={closeDrawer} />}

              {showIntercom && <IntercomNavItem key='intercom-item' />}

              <UserGuideNavItem key='user-guide-item' />

              {!hideNavButtons && <SettingsPage key='settings-page' closeDrawer={closeDrawer} />}

              <ProfileMenu key='profile-menu' hideFeatures={hideNavButtons} />
            </div>
          </div>
        </div>
      </AppBar>
    </Slide>
  );
}

export default memo(HeaderNew);
